/**
 * SOLID Review
 * S — Strong. One concern: the account-deletion flow (GDPR Art. 17) — confirm, a verification code sent, the code entered, the
 *     deletion — its busy flags and its message.
 * O — Good. A new step is one more flag and its branch in the view; not a change shared with the export flow.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The flow's five actions.
 * D — Strong. Two use cases and the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import type { ClientRequests } from '../client/ClientRequests';
import { messageFor } from '../client/failureMessage';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const REQUEST_CODE: TClientUseCaseInput<'requestAccountDeletion'> = {};

export interface IStateGdprDeletion {
  readonly deleting: boolean;
  readonly deleteError: string | null;
  readonly confirmDelete: boolean;
  readonly requestingCode: boolean;
  readonly codeRequested: boolean;
  readonly deletionCode: string;
}

const START: IStateGdprDeletion = {
  deleting: false, deleteError: null, confirmDelete: false, requestingCode: false, codeRequested: false, deletionCode: '',
};
const CONFIRMING: Partial<IStateGdprDeletion> = { confirmDelete: true };
const REQUESTING: Partial<IStateGdprDeletion> = { requestingCode: true, deleteError: null };
const CODE_SENT: Partial<IStateGdprDeletion> = { codeRequested: true };
const REQUESTED: Partial<IStateGdprDeletion> = { requestingCode: false };
const DELETING: Partial<IStateGdprDeletion> = { deleting: true, deleteError: null };
const DELETED: Partial<IStateGdprDeletion> = { confirmDelete: false, codeRequested: false, deletionCode: '' };
const DONE: Partial<IStateGdprDeletion> = { deleting: false };
const CANCELLED: Partial<IStateGdprDeletion> = { confirmDelete: false, codeRequested: false, deletionCode: '', deleteError: null };

/**
 * ACCOUNT DELETION (frontend-framework-oo P3, 2026-10-29, §28.295) — `legal/components/tabs/GdprDeletionSection.tsx`'s state and
 * handlers. The account deleted is a signed-out tab: the use case signs out too (strict-ports FE4a); the view's own `onAccountDeleted`
 * is called once the flow has reset.
 */
export class GdprDeletionViewModel implements IServiceViewModel<IStateGdprDeletion> {
  private readonly state = new ObservableState<IStateGdprDeletion>(START);

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'requestAccountDeletion' | 'deleteAccount'>,
    private readonly requests: ClientRequests,
  ) {}

  readonly askToConfirm = (): void => this.state.update(CONFIRMING);

  readonly requestCode = async (): Promise<void> => {
    this.state.update(REQUESTING);
    try {
      const sent = await this.useCases.requestAccountDeletion(this.requests.context(), REQUEST_CODE);
      if (!sent.success) {
        const failed: Partial<IStateGdprDeletion> = { deleteError: messageFor(sent.error, 'Failed to send verification code.') };
        this.state.update(failed);
      } else {
        this.state.update(CODE_SENT);
      }
    } finally {
      this.state.update(REQUESTED);
    }
  };

  /** Digits only, four at most. */
  readonly editCode = (typed: string): void => {
    const code: Partial<IStateGdprDeletion> = { deletionCode: typed.replace(/\D/g, '').slice(0, 4) };
    this.state.update(code);
  };

  readonly confirm = async (onAccountDeleted?: () => void): Promise<void> => {
    this.state.update(DELETING);
    try {
      const command: TClientUseCaseInput<'deleteAccount'> = { code: this.state.get().deletionCode };
      const deleted = await this.useCases.deleteAccount(this.requests.context(), command);
      if (!deleted.success) {
        const failed: Partial<IStateGdprDeletion> = { deleteError: messageFor(deleted.error, 'Account deletion failed.') };
        this.state.update(failed);
        return;
      }
      this.state.update(DELETED);
      onAccountDeleted?.();
    } finally {
      this.state.update(DONE);
    }
  };

  readonly cancel = (): void => this.state.update(CANCELLED);

  getSnapshot(): IStateGdprDeletion {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {}
}
